import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Buymeacoffee from "../components/buymeacoffee";
import { useAuth } from "../contexts/authContext";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import FeedbackForm from "../components/FeedbackForm";

/**
 * 심사 최적화 노트
 * - 링크 모음형 대시보드 대신, 텍스트 중심의 풍부한 콘텐츠 섹션을 추가했습니다.
 * - Hero(소개) / 서비스별 상세 / 최신 소식 / FAQ / Footer 구조로 개편.
 * - 준비중인(Empty) 항목은 심사 중 노출하지 않도록 제거.
 */

const services = [
  {
    key: "malhaejo",
    label: "말해조 (Malhaejo)",
    to: "/home",
    badge: "문장 보정 · 공손체",
    summary:
      "핵심만 적어도 비즈니스 상황에 맞는 정중체로 다듬어 주는 AI 문장 도우미입니다. 메일·공지·사과문·요청문 등 다양한 상황별 템플릿을 바탕으로 어투/톤을 자동 조정합니다.",
    exampleTitle: "사용 예시",
    example:
      "초안: ‘보고서 제출이 늦어질 것 같습니다.’ → 결과: ‘보고서 제출이 다소 지연될 예정입니다. 양해 부탁드립니다.’",
  },
  {
    key: "calc",
    label: "계산해 (CalcForMe)",
    to: "/calc-helper",
    badge: "자연어 계산 · 환율/이자",
    summary:
      "자연어로 입력한 문장을 계산식으로 해석해 결과를 알려주는 AI 계산기입니다. 할인/부가세/환율/단위변환/이자 등 복잡한 계산을 직관적으로 처리합니다.",
    exampleTitle: "사용 예시",
    example:
      "‘23500원을 30% 할인하면?’ → 결과와 함께 계산 과정을 함께 표시해 이해를 돕습니다.",
  },
  {
    key: "brain",
    label: "두뇌트레이너 (BrainTrainer)",
    to: "/brain-trainer",
    badge: "상식 · 산수 · 기억력 훈련",
    summary:
      "10대부터 70대까지 연령별 맞춤 퀴즈로 두뇌를 가볍게 자극해 보세요. 난이도 선택, 이야기 기억력, 사자성어 등 다양한 유형과 ‘뇌 나이’ 측정 기능을 제공합니다.",
    exampleTitle: "사용 예시",
    example:
      "3분 퀴즈를 풀고 즉시 결과와 ‘뇌 나이’ 리포트를 확인합니다. 로그인 시 기록이 저장되어 추세를 확인할 수 있습니다.",
  },
  {
    key: "rumble",
    label: "Rumble Chatbot",
    to: "/chat",
    badge: "게임 가이드(비공식)",
    summary:
      "디지몬 슈퍼럼블 정보를 바탕으로 제작한 비공식 가이드 챗봇입니다. 직접 플레이 경험을 반영해 퀘스트/장비/육성 등 질문에 응답합니다. (공식 개발사와 무관)",
    exampleTitle: "사용 예시",
    example:
      "‘초반 육성 루트 추천’처럼 구체적인 질문을 입력하면 단계별 가이드를 제공합니다.",
  },
  {
    key: "translator",
    label: "스크린번역기 (Screen Translator)",
    to: "/screen-translator",
    badge: "다운로드 · OCR 번역",
    summary:
      "화면 위 특정 영역을 드래그하여 원하는 언어로 번역하는 데스크톱 유틸리티입니다.",
    exampleTitle: "사용 예시",
    example:
      "게임/동영상/문서 화면의 텍스트 부분을 감싸고 ‘번역’ 클릭 → 결과 오버레이 표시.",
  },
];

const updates = [
  { date: "2025-08-10", title: "스크린번역기 (Screen Translator) 항목 추가", desc: "화면 위 특정 영역을 드래그하여 원하는 언어로 번역하는 데스크톱 유틸리티입니다." },
  { date: "2025-08-10", title: "J_Universe – 메인페이지 UI 개선", desc: "메인 페이지 구조 및 콘텐츠 강화." },
  { date: "2025-08-02", title: "두뇌트레이너 – 난이도 선택 및 이야기 가독성 개선", desc: "지문/문제 분리, 5초 지문 보기 기능 등 UX 개선." },
  { date: "2025-08-01", title: "말해조 – 상황형 템플릿 확장", desc: "업무 상황별 템플릿 추가." },
];

export default function MainPage() {
  const navigate = useNavigate();
  const { isLoggedIn, setIsLoggedIn, userEmail, setUserEmail } = useAuth();
  const [announcement, setAnnouncement] = useState("");
  const [userNickname, setUserNickname] = useState("");
  const [remainingCalls, setRemainingCalls] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [usage, setUsage] = useState({
    mode: "-",
    daily_free_remaining: 0,
    subscription_remaining: 0,
    prepaid_remaining: 0,
    total_remaining: 0,
  });

  // 로그인 사용자 닉네임 조회
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        setUserNickname("");
        return;
      }
      const res = await fetchWithAuth("/users/me");
      if (res && res.ok) {
        const data = await res.json();
        setUserNickname(data.nickname);
        localStorage.setItem("nickname", data.nickname);
      } else {
        setUserNickname("");
      }
    };
    checkAuth();
  }, []);

  // 공지 불러오기(재시도 포함)
  useEffect(() => {
    const MAX_RETRIES = 3;
    const RETRY_DELAY = 5000;
    const fetchAnnouncement = (retryCount = 0) => {
      fetchWithAuth(`/announcement`)
        .then((res) => {
          if (!res.ok) throw new Error("Response not ok");
          return res.json();
        })
        .then((data) => {
          if (data.message) setAnnouncement(data.message);
        })
        .catch((err) => {
          console.warn(`공지사항 가져오기 실패 (시도 ${retryCount + 1}):`, err);
          if (retryCount < MAX_RETRIES) {
            setTimeout(() => fetchAnnouncement(retryCount + 1), RETRY_DELAY);
          }
        });
    };
    fetchAnnouncement();
  }, []);

  // 남은 호출 횟수 조회
  useEffect(() => {
    const token = localStorage.getItem("token");
    const anonId = localStorage.getItem("anon_id");
    fetchWithAuth(`/gpt-usage`)
      .then((res) => res.json())
      .then((data) => setRemainingCalls(data.remaining))
      .catch((err) => console.error("사용 가능 횟수 조회 실패", err));
  }, []);

  useEffect(() => {
    fetchWithAuth(`/gpt-usage/detail`)
      .then((res) => res.json())
      .then((data) => setUsage(data))
      .catch((err) => console.error("상세 사용량 조회 실패", err));
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("email");
    localStorage.removeItem("nickname");
    localStorage.removeItem("usage_count");
    localStorage.removeItem("daily_limit");
    setIsLoggedIn(false);
    setUserEmail("");
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* 헤더 + 글로벌 알림 */}
      <header className="w-full border-b border-gray-800">
        <div className="mx-auto max-w-5xl px-4 py-4 flex items-center justify-between">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-wide">J_Universe</h1>
          <nav className="flex items-center gap-2">
            {!isLoggedIn && (
              <>
                <Link
                  to="/login"
                  className="px-3 py-1.5 text-sm font-semibold bg-blue-500 rounded-full"
                >
                  Sign in
                </Link>
                <Link
                  to="/signup"
                  className="px-3 py-1.5 text-sm font-semibold bg-green-500 rounded-full"
                >
                  Sign up
                </Link>
              </>
            )}
            {isLoggedIn && (
              <>
                <span className="text-sm text-green-300">어서오세요, <span className="text-yellow-300 font-semibold">{userNickname}</span> 님</span>
                <Link
                  to="/mypage"
                  className="px-3 py-1.5 text-sm font-semibold bg-emerald-600 rounded-full"
                >
                  MyPage
                </Link>
                <button
                  onClick={handleLogout}
                  className="px-3 py-1.5 text-sm font-semibold bg-red-500 rounded-full"
                >
                  Sign out
                </button>
              </>
            )}
            {isLoggedIn && userEmail === "ljw@naver.com" && userNickname === "관리자" && (
              <Link
                to="/admin"
                className="px-3 py-1.5 text-sm font-semibold bg-purple-600 rounded-full"
              >
                관리자페이지
              </Link>
            )}
          </nav>
        </div>
        {announcement && (
          <div className="mx-auto max-w-5xl px-4 pb-4">
            <div className="w-full bg-yellow-200 text-yellow-900 text-center py-2 px-4 rounded-md shadow border border-yellow-400">
              📢 {announcement}
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="mx-auto max-w-5xl px-4 py-10">
        <div className="text-center">
          <h2 className="text-3xl sm:text-5xl font-extrabold mb-4 leading-tight">
            AI로 일상을 더 편리하게 <span className="text-blue-400">J_Universe</span>
          </h2>
          <p className="text-gray-300 max-w-2xl mx-auto">
            말해조, 계산해, 두뇌트레이너, RumbleChatbot, 스크린번역기 등 AI 기반 서비스로 여러분의 작업과 학습, 커뮤니케이션을 도와드립니다. 아래에서 각 서비스를 자세히 살펴보세요.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              className="px-4 py-2 rounded-full bg-green-700 hover:bg-green-600 font-semibold"
              onClick={() => setShowFeedback(true)}
            >
              피드백 보내기
            </button>
          </div>
          {/* 사용량 요약 뱃지 */}
          <div className="mt-6">
            <div className="grid grid-cols-3 gap-2 max-w-md mx-auto">
              <Badge label="오늘 무료" value={usage.daily_free_remaining} />
              <Badge label="구독 잔여" value={usage.subscription_remaining} />
              <Badge label="충전 잔여" value={usage.prepaid_remaining} />
            </div>
            <p className="mt-3 text-sm text-yellow-300 font-semibold">
              총 합계: {usage.total_remaining}회
            </p>
            <button
              onClick={() => navigate("/usage")}
              className="mt-2 text-xs underline text-gray-300 hover:text-white"
            >
              상세 보기
            </button>
          </div>
        </div>
      </section>

      {/* Services Section – 서비스별 상세 소개 */}
            <section className="mx-auto max-w-5xl px-4 pb-6">
        <h3 className="text-2xl sm:text-3xl font-bold mb-6">주요 서비스</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {services.map((s) => (
            <article key={s.key} className="bg-gray-800 border border-gray-700 rounded-2xl p-5 shadow">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xl font-semibold">{s.label}</h4>
                <span className="text-xs bg-gray-700 px-2 py-1 rounded-full">{s.badge}</span>
              </div>
              <p className="text-gray-300 leading-relaxed">{s.summary}</p>
              <div className="mt-3 text-sm bg-gray-900 border border-gray-700 rounded-lg p-3">
                <div className="font-semibold mb-1">{s.exampleTitle}</div>
                <p className="text-gray-300">{s.example}</p>
              </div>
              <div className="mt-4 flex gap-2">
                <button onClick={() => navigate(s.to)} className="px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-sm font-semibold">시작하기</button>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Updates / Blog-like Section */}
      <section className="mx-auto max-w-5xl px-4 py-6">
        <h3 className="text-2xl sm:text-3xl font-bold mb-4">최신 소식</h3>
        <ul className="space-y-3">
          {updates.map((u, i) => (
            <li key={i} className="bg-gray-800 border border-gray-700 rounded-xl p-4">
              <div className="text-xs text-gray-400">{u.date}</div>
              <div className="font-semibold mt-1">{u.title}</div>
              <p className="text-gray-300 text-sm mt-1">{u.desc}</p>
            </li>
          ))}
        </ul>
      </section>

      {/* FAQ Section */}
      <section className="mx-auto max-w-5xl px-4 py-6">
        <h3 className="text-2xl sm:text-3xl font-bold mb-4">자주 묻는 질문</h3>
        <div className="space-y-4">
          <details className="bg-gray-800 border border-gray-700 rounded-xl p-4" open>
            <summary className="font-semibold cursor-pointer">무료로 사용할 수 있나요?</summary>
            <p className="text-gray-300 mt-2 text-sm">
              로그인 없이 하루 3회 무료로 이용 가능합니다. 회원은 하루 6회까지 사용하실 수 있으며, 매일 00시 00분에 초기화됩니다.
            </p>
          </details>
          <details className="bg-gray-800 border border-gray-700 rounded-xl p-4">
            <summary className="font-semibold cursor-pointer">모바일에서도 사용 가능한가요?</summary>
            <p className="text-gray-300 mt-2 text-sm">
              네. 주요 서비스는 모바일 브라우저에서도 이용하실 수 있도록 반응형으로 구성되어 있습니다.
            </p>
          </details>
          <details className="bg-gray-800 border border-gray-700 rounded-xl p-4">
            <summary className="font-semibold cursor-pointer">개발사/저작권 관련 안내</summary>
            <p className="text-gray-300 mt-2 text-sm">
              Rumble Chatbot은 디지몬 슈퍼럼블의 비공식 팬 메이드 도구로, 공식 개발사와는 관련이 없습니다. 각 상표와 저작권은 해당 소유자에게 귀속됩니다.
            </p>
          </details>
          <details className="bg-gray-800 border border-gray-700 rounded-xl p-4">
            <summary className="font-semibold cursor-pointer">스크린번역기(ScreenTranslator)  사용 안내</summary>
            <p className="text-gray-300 mt-2 text-sm">
              스크린번역기(Screen Translator)는 비회원은 이용이 불가합니다. 로그인 후 일일 무료 횟수 및 남은 횟수로 이용이 가능합니다.
            </p>
          </details>
        </div>
      </section>

      {/* Feedback & Donation */}
      <section className="mx-auto max-w-5xl px-4 py-6 text-center">
        <button
          className="px-4 py-2 bg-blue-500 hover:bg-blue-400 rounded-lg"
          onClick={() => setShowFeedback(true)}
        >
          Feedback
        </button>
        {showFeedback && <FeedbackForm onClose={() => setShowFeedback(false)} />}

        <div className="mt-8">
          <Buymeacoffee />
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-800">
        <div className="mx-auto max-w-5xl px-4 py-8 text-gray-400 text-sm">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p>
              © {new Date().getFullYear()} J_Universe. 문의: <a href="mailto:ckrgksrleh@google.com" className="underline">ckrgksrleh@google.com</a>
            </p>
            <div className="flex gap-4">
              <Link to="/signup" className="hover:text-gray-200">개인정보 처리방침</Link>
              <Link to="/signup" className="hover:text-gray-200">이용약관</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Badge({ label, value }) {
  return (
    <div className="flex flex-col items-center rounded-lg border border-gray-700 p-3 bg-gray-800">
      <div className="text-xs text-gray-400">{label}</div>
      <div className="text-xl font-bold text-white">{value}</div>
    </div>
  );
}
